import { type ColumnDef } from "@tanstack/react-table";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RemoteCombobox } from "@/components/ui/remote-combobox";
import { PageContainer, PageHeader } from "@/components/layout";
import AddResourceDialog from "../../components/resource/add-resource-dialog";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { ResourceSheet } from "../../components/resource/resource-sheet";
import type { ResourceRowForSheet } from "../../components/resource/resource-sheet";
import FiltersCard from "../../components/filters-card";
import { TagsMultiSelect } from "../../components/tags-multi-select";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { Permissions } from "../../entities/permissions";
import { hasPermission } from "../login/selectors";
import { useQueryState } from "../../utils/query-state";
import {
  useDeleteResourceMutation,
  useResourceListQuery,
} from "../../services/api";

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface ResourceRow {
  id: string;
  name: string;
  tags?: TagRef[];
  [key: string]: unknown;
}

interface ResourcesResponse {
  items?: ResourceRow[];
  itemCount?: number;
  range?: { limit?: number; page?: number };
}

interface AutocompleteValue {
  id?: string | number;
  [key: string]: unknown;
}

export default function Resources() {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const hasAddPermission = useSelector(
    hasPermission(Permissions.PAGE_RESOURCES_ADD)
  );
  const hasEditPermission = useSelector(
    hasPermission(Permissions.PAGE_RESOURCES_EDIT)
  );
  const hasRemovePermission = useSelector(
    hasPermission(Permissions.PAGE_RESOURCES_REMOVE)
  );

  const [openAddResourceDialog, setOpenAddResourceDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ResourceRow | null>(null);
  // Sheet open state lives in the URL via `?detail=<id>`, so refresh and
  // shared links restore the sheet. `editOnOpen` is a transient one-shot
  // signal: when the user clicks the row's Pencil icon we want the sheet
  // to open straight into edit mode; otherwise it starts in read mode.
  const [selectedDetailId, setSelectedDetailId] = useQueryState({
    paramName: "detail",
    fromQuery: (value: string | null) => value || null,
    toQuery: (value: string | null) => (value ? String(value) : ""),
    defaultValue: null,
  }) as [string | null, (next: string | null) => void];
  const [editOnOpen, setEditOnOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<Record<string, string | number[] | undefined> | null>(
    null
  );

  // Filter form state (committed to `filters` only on Search).
  const [resource, setResource] = useState<AutocompleteValue | null>(null);
  const [filterTags, setFilterTags] = useState<TagRef[]>([]);

  const computedFilters = useMemo(() => {
    const obj: Record<string, unknown> = {
      id: resource?.id != null ? String(resource.id) : undefined,
      tagIds: filterTags.length > 0 ? filterTags.map((tag) => tag.id) : undefined,
    };
    Object.keys(obj).forEach((key) => {
      if (obj[key] === undefined) delete obj[key];
    });
    return obj as Record<string, string | number[] | undefined>;
  }, [resource, filterTags]);

  function onSearch() {
    setFilters(computedFilters);
    setPage(0);
  }

  function onCancel() {
    setResource(null);
    setFilterTags([]);
    setFilters(null);
    setPage(0);
  }

  const { data: resources, isFetching: fetchingList } = useResourceListQuery({
    range: { page: page + 1 },
    filters: filters ?? undefined,
  }) as { data?: ResourcesResponse; isFetching: boolean };
  const [deleteResource, { isLoading: isDeleting }] = useDeleteResourceMutation();

  // Resolve the selected detail row from the current list page.
  const selectedRow = useMemo(() => {
    if (!selectedDetailId) return null;
    return (
      resources?.items?.find((row) => String(row.id) === selectedDetailId) ??
      null
    );
  }, [selectedDetailId, resources]);

  // Fallback: a deep-linked id (e.g. opened from search) may not be on the
  // current list page. Once the list has settled without finding it, fetch that
  // single resource directly (the list endpoint filters by id) so the sheet
  // resolves regardless of pagination instead of staying empty.
  const needsFallback =
    selectedDetailId != null && !selectedRow && !fetchingList;
  const { data: fallbackResources, isFetching: fetchingFallback } =
    useResourceListQuery(
      {
        range: { page: 1 },
        filters: selectedDetailId ? { id: selectedDetailId } : undefined,
      },
      { skip: !needsFallback },
    ) as { data?: ResourcesResponse; isFetching: boolean };
  const fallbackRow = useMemo(() => {
    if (!needsFallback) return null;
    return (
      fallbackResources?.items?.find(
        (row) => String(row.id) === selectedDetailId,
      ) ?? null
    );
  }, [needsFallback, fallbackResources, selectedDetailId]);

  const resolvedRow = selectedRow ?? fallbackRow;

  function onRowClick(row: ResourceRow) {
    setEditOnOpen(false);
    setSelectedDetailId(String(row.id));
  }

  function onEdit(row: ResourceRow) {
    setEditOnOpen(true);
    setSelectedDetailId(String(row.id));
  }

  function onDelete(row: ResourceRow) {
    setDeleteTarget(row);
  }

  async function handleDelete() {
    if (!deleteTarget?.id) return;
    try {
      await deleteResource(deleteTarget.id).unwrap();
      const deletedName = deleteTarget.name;
      setDeleteTarget(null);
      // If the sheet was open on this row, close it too.
      if (selectedDetailId === String(deleteTarget.id)) {
        setSelectedDetailId(null);
      }
      showNotification(
        "success",
        t("resource.delete-resource-dialog.success", { name: deletedName }),
      );
    } catch {
      showNotification("error", t("common.api-error"));
    }
  }

  // No per-row Edit or Delete columns — row click opens the sheet in
  // view mode; Edit / Delete affordances live in the sheet header. Bulk
  // delete remains via the future multi-select toolbar.
  const columns = useMemo<ColumnDef<ResourceRow>[]>(() => [
    {
      accessorKey: "name",
      header: t("resource.items.identifier"),
    },
    {
      id: "tags",
      header: t("resource.items.tags"),
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {(row.original.tags ?? []).map((tag) => (
            <Badge
              key={tag.id}
              style={{
                backgroundColor: tag.color ?? undefined,
                color: tag.color ? "#fff" : undefined,
              }}
            >
              {tag.name}
            </Badge>
          ))}
        </div>
      ),
    },
  ], [t]);

  const rows = resources?.items ?? [];
  const pageSize = resources?.range?.limit ?? 15;
  const pageCount = resources?.itemCount
    ? Math.max(1, Math.ceil(resources.itemCount / pageSize))
    : 1;

  return (
    <>
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(next) => { if (!next) setDeleteTarget(null); }}
        title={t("resource.delete-resource-dialog.title", {
          name: deleteTarget?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        onConfirm={handleDelete}
        isPending={isDeleting}
      />

      <AddResourceDialog
        title={t("resource.add-resource-dialog.title")}
        open={openAddResourceDialog}
        onClose={() => setOpenAddResourceDialog(false)}
        onCreated={() => setOpenAddResourceDialog(false)}
      />

      <ResourceSheet
        value={resolvedRow as ResourceRowForSheet | null}
        open={selectedDetailId != null}
        // Deep-linked (e.g. from search) before the row is resolved — from the
        // list page or the by-id fallback: show skeletons instead of an empty
        // panel until the data lands.
        loading={
          selectedDetailId != null &&
          !resolvedRow &&
          (fetchingList || fetchingFallback)
        }
        onOpenChange={(next) => { if (!next) setSelectedDetailId(null); }}
        canEdit={hasEditPermission}
        initialEditing={editOnOpen}
        onEdited={() => {
          showNotification(
            "success",
            t("resource.edit-resource-dialog.success", "Resource updated"),
          );
        }}
        onDelete={
          hasRemovePermission && resolvedRow
            ? () => onDelete(resolvedRow)
            : undefined
        }
      />

      <PageContainer title={t("resource.tab-title")}>
        <PageHeader
          title={t("resource.page-title")}
          description={t("resource.info-message")}
          actions={
            hasAddPermission ? (
              <Button onClick={() => setOpenAddResourceDialog(true)}>
                <PlusIcon className="size-4" />
                {t("resource.add-resource")}
              </Button>
            ) : null
          }
        />

        <div className="mt-6">
          {/* AutocompleteInput is still a MUI primitive shared with
              un-migrated pages; renders against the MUI theme adapter.
              FiltersCard itself is now shadcn (Card + Button), so the
              chrome around these inputs is consistent with the rest of
              the migrated UI. */}
          <FiltersCard onSearch={onSearch} onCancel={onCancel}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <RemoteCombobox
                resource="resource/name-autocomplete"
                value={resource}
                onChange={setResource}
                label={t("inspections.filter.resource")}
              />
              <TagsMultiSelect
                value={filterTags}
                onChange={setFilterTags}
                label={t("resource.filter.tags")}
                placeholder={t("tags.multi-select.placeholder")}
                mode="filter"
              />
            </div>
          </FiltersCard>
        </div>

        <div className="mt-6">
          <DataTable
            columns={columns}
            data={rows}
            emptyMessage={t("common.no-data", "No data")}
            onRowClick={onRowClick}
            loading={fetchingList}
            skeletonRows={pageSize}
            manualPagination={{
              pageIndex: page,
              pageCount,
              onPageChange: setPage,
            }}
          />
        </div>
      </PageContainer>
    </>
  );
}
